import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { route } from '~/app/helpers/route';
import { MACHINES_DETAILS_ROUTES } from '~/machines/Details/Router';
import Icon from '~/app/common/Icon';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { CommunicationRoleUser, Machine, Project } from '~/types';
import { getInitials } from '../store';
import classes from './style.module.scss';

export type RoleUsersComponent = React.FC;

const RoleUsers: RoleUsersComponent = () => {
    const { t } = useTranslation('communication');
    const history = useHistory();
    const { setData } = useAppHeader();
    const { id, projectId, roleId } = useParams<{
        id: Machine['id']
        projectId: Project['id']
        roleId: string
    }>();

    const machine = useStoreState(state => state.machine.data);
    const currentUserId = useStoreState(state => state.user.data.id);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const roles = useStoreState(state => state.communication.roles);
    const fetchRoles = useStoreActions(actions => actions.communication.fetchRoles);
    const prepareOpenChat = useStoreActions(actions => actions.communicationChat.prepareOpenChat);

    usePageTitle(t('pageTitle'));

    useEffect(() => {
        setData({
            title: t('pageTitle'),
            subTitle: `${machine.intern || ''} ${machine.name || ''}`.trim().toUpperCase(),
            backLink: () => history.push(route(MACHINES_DETAILS_ROUTES.communication, { id, projectId })),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, t, machine, history, id, projectId]);

    useEffect(() => {
        if (netWorkStatus !== 0) {
            return;
        }
        if (!roles.length) {
            fetchRoles({ machineId: id, projectId });
        }
    }, [id, projectId, netWorkStatus, roles.length, fetchRoles]);

    const role = useMemo(() => {
        const numericRoleId = Number(roleId);
        return roles.find(item => item.id === numericRoleId) || null;
    }, [roles, roleId]);

    const machineLabel = (machine.name || machine.intern || '').toString();
    const users = useMemo(
        () => (role?.users || []).filter(user => Number(user.id) !== Number(currentUserId)),
        [role?.users, currentUserId],
    );
    const showSendToAll = users.length > 1;

    const sendToAllLabel = useMemo(() => {
        const name = (role?.name || '').toLocaleLowerCase();
        return t('sendToAll', { roleName: name });
    }, [role, t]);

    const openChat = useCallback((userIds: number[], title: string) => {
        if (!role) {
            return;
        }
        const recipients = users
            .filter(u => userIds.includes(u.id))
            .map(u => ({ id: u.id, name: u.name }));

        prepareOpenChat({
            machineId: id,
            projectId,
            roleId: role.id,
            userIds,
            recipients,
            title,
            roleName: role.name,
        });
        history.push(route(MACHINES_DETAILS_ROUTES.communicationChat, { id, projectId }));
    }, [history, id, projectId, role, users, prepareOpenChat]);

    const onSelectUser = useCallback((user: CommunicationRoleUser) => {
        openChat([user.id], user.name);
    }, [openChat]);

    const onSendToAll = useCallback(() => {
        openChat(users.map(user => user.id), role?.name || t('pageTitle'));
    }, [openChat, users, role, t]);

    if (!role) {
        return (
            <AccessControl requiredPermissions={PERMISSION_GROUPS.machineRegistration}>
                <div className={classes['role-users-page']} />
            </AccessControl>
        );
    }

    return (
        <AccessControl requiredPermissions={PERMISSION_GROUPS.machineRegistration}>
            <div className={classes['role-users-page']}>
                <div className={classes['role-title']}>{role.name}</div>
                <div className={classes['context']}>
                    {t('recipientContext', {
                        roleName: (role.name || '').toLocaleLowerCase(),
                        machineName: machineLabel.toLocaleLowerCase()
                    })}
                </div>
                <div>
                    {users.map(user => (
                        <div
                            key={user.id}
                            className={classes['user-row']}
                            onClick={() => onSelectUser(user)}
                        >
                            <div className={classes['avatar']}>{getInitials(user.name)}</div>
                            <div className={classes['info']}>
                                <div className={classes['title']}>{user.name}</div>
                            </div>
                            <div className={classes['right-icon']}>
                                <Icon name="keyboard_arrow_right" />
                            </div>
                        </div>
                    ))}

                    {showSendToAll && (
                        <div className={classes['user-row']} onClick={onSendToAll}>
                            <div className={classes['avatar']}>
                                <Icon name="people" />
                            </div>
                            <div className={classes['info']}>
                                <div className={classes['title']}>{sendToAllLabel}</div>
                            </div>
                            <div className={classes['right-icon']}>
                                <Icon name="keyboard_arrow_right" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AccessControl>
    );
};

export default RoleUsers;
